"use client";

import {
  useRoiCalculator,
  type RoiCalculatorInputs as RoiInputs,
} from "@marketing-sites/hooks/use-roi-calculator";
import { cn } from "@marketing-sites/utils/cn";
import { RoiCalculatorInputs } from "./inputs";
import { RoiCalculatorResults } from "./results";

export type RoiCalculatorInitialInputs = Partial<
  Pick<
    RoiInputs,
    | "conversationsPerMonth"
    | "numberOfCustomerSupportEmployees"
    | "annualCostPerEmployee"
  >
>;

export interface RoiCalculatorProps {
  /** Starting values for the inputs. Blank fields fall back to the fin.ai/roi-calculator defaults. */
  initialInputs?: RoiCalculatorInitialInputs;
  /** "split" puts inputs beside results at lg; "stacked" keeps them above results for narrow columns. */
  layout?: "split" | "stacked";
  /** CSS colour for the slider fill. Defaults to Fin orange. */
  accentColor?: string;
  /** Tailwind text utility for the headline savings figure. Defaults to Fin orange. */
  accentTextClassName?: string;
  openMetadata: { analyticsId: string };
  closeMetadata: { analyticsId: string };
  className?: string;
}

// Flat resolution rate to match fin.ai/roi-calculator; module-level keeps the hook's memo stable.
const ROI_CONFIG = { monthlyResolutionRateIncrease: 0 };

export function RoiCalculator({
  initialInputs,
  layout = "split",
  accentColor = "var(--color-fin)",
  accentTextClassName = "text-fin",
  openMetadata,
  closeMetadata,
  className,
}: RoiCalculatorProps) {
  const {
    conversationsPerMonth,
    numberOfCustomerSupportEmployees,
    annualCostPerEmployee,
    resolutionRate,
    finResolutionRate,
    setConversationsPerMonth,
    setNumberOfCustomerSupportEmployees,
    setAnnualCostPerEmployee,
    setResolutionRate,
    roiCalculationResult,
  } = useRoiCalculator(initialInputs, ROI_CONFIG);

  return (
    <div
      className={cn(
        "border-current/10 grid grid-cols-1 gap-10 border p-4 md:p-6",
        layout === "split" &&
          "lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] lg:gap-12",
        className,
      )}
    >
      <RoiCalculatorInputs
        conversationsPerMonth={conversationsPerMonth}
        supportAgents={numberOfCustomerSupportEmployees}
        annualCostPerAgent={annualCostPerEmployee}
        resolutionRate={resolutionRate}
        averageResolutionRate={finResolutionRate}
        onConversationsPerMonthChange={setConversationsPerMonth}
        onSupportAgentsChange={setNumberOfCustomerSupportEmployees}
        onAnnualCostPerAgentChange={setAnnualCostPerEmployee}
        onResolutionRateChange={setResolutionRate}
        accentColor={accentColor}
        openMetadata={openMetadata}
        closeMetadata={closeMetadata}
        className={
          layout === "stacked" ? "md:grid md:grid-cols-2 md:gap-x-8" : undefined
        }
      />
      <RoiCalculatorResults
        result={roiCalculationResult}
        accentTextClassName={accentTextClassName}
      />
    </div>
  );
}
